import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Feather } from "lucide-react";
import { SectionHeading, Reveal, EASE } from "./Reveal";
import { api } from "../lib/api";

function Reader({ post, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis && window.__lenis.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis && window.__lenis.start();
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] bg-white overflow-y-auto"
      data-testid="journal-reader"
    >
      <div className="sticky top-0 z-10 glass-nav">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 h-14 flex items-center justify-between">
          <p className="text-sm font-semibold text-[#1d1d1f]">Trend Journal</p>
          <button onClick={onClose} aria-label="Close article" className="p-2 rounded-full hover:bg-black/5 transition-colors">
            <X size={20} />
          </button>
        </div>
      </div>
      <motion.article
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="max-w-3xl mx-auto px-4 sm:px-8 pt-12 pb-24"
      >
        {post.cover && (
          <img src={post.cover} alt={post.title} className="w-full h-64 sm:h-80 object-cover rounded-3xl" />
        )}
        <p className="mt-10 text-xs uppercase tracking-[0.22em] font-semibold text-[#0066cc]">{post.season}</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1d1d1f] leading-tight">{post.title}</h1>
        <p className="mt-3 text-sm text-[#a1a1a6]">
          {new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · Sundeepa Bamval
        </p>
        <div className="mt-8 flex flex-col gap-5 text-lg text-[#1d1d1f]/85 leading-relaxed">
          {post.body.split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </motion.article>
    </motion.div>
  );
}

export default function Journal() {
  const [posts, setPosts] = useState(null);
  const [reading, setReading] = useState(null);

  useEffect(() => {
    api.get("/journal").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  return (
    <section id="journal" className="py-20 sm:py-28 lg:py-32 scroll-mt-16" data-testid="journal-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <SectionHeading
          eyebrow="Trend Journal"
          title="Seasonal notes from the studio"
          subtitle="Short notes on India's fashion rhythm — colour direction, calendar planning and what the next quarter should prepare for."
        />

        {posts === null ? (
          <div className="py-20 flex justify-center text-[#86868b]"><Loader2 className="animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="mt-12 rounded-[28px] border border-dashed border-[#d2d2d7] bg-white/60 py-20 text-center" data-testid="journal-empty-state">
            <Feather className="mx-auto text-[#d4af37]" size={28} />
            <p className="mt-4 text-lg font-medium text-[#1d1d1f]">The first seasonal note is being written</p>
            <p className="mt-1 text-sm text-[#86868b]">Check back shortly — or ask Studio SB about the current season.</p>
          </div>
        ) : (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08} className="h-full">
                <button
                  onClick={() => setReading(p)}
                  data-testid={`journal-card-${p.id.slice(0, 8)}`}
                  className="group text-left w-full h-full bg-white rounded-3xl border border-[#e5e5e7] overflow-hidden hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 flex flex-col"
                >
                  {p.cover ? (
                    <div className="h-44 overflow-hidden">
                      <img src={p.cover} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                  ) : (
                    <div className="h-44 bg-[#161617] flex items-center justify-center">
                      <span className="font-serif-editorial italic text-4xl text-[#d4af37]">SB</span>
                    </div>
                  )}
                  <div className="p-7 flex-1 flex flex-col">
                    <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#0066cc]">{p.season}</p>
                    <h3 className="mt-2.5 text-xl font-semibold tracking-tight text-[#1d1d1f] leading-snug">{p.title}</h3>
                    <p className="mt-3 flex-1 text-[15px] text-[#86868b] leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <p className="mt-5 text-xs text-[#a1a1a6]">
                      {new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>{reading && <Reader post={reading} onClose={() => setReading(null)} />}</AnimatePresence>
    </section>
  );
}
